<template>
  <div class="page-section tu-scope">
    <div :class="containerClass">
      <h2 class="mb-2">Schools</h2>
      <p class="mb-4">Find the schools with the nursing programs you want.</p>

      <div class="tu-toolbar" role="search" aria-label="Filter schools">
        <tu-filter-search
          :value="searchTerm"
          placeholder="Search by school name"
          label="Search schools by name"
          @input="onSearchInput"
        />
        <tu-filter-select
          :value="filters.program_id"
          label="Program"
          placeholder="All programs"
          :options="programOptions"
          aria-label="Filter by program"
          @change="filters.program_id = $event || null; fetchUniversities()"
        />
        <tu-filter-select
          :value="filters.state"
          label="State"
          placeholder="All states"
          :options="stateOptions"
          aria-label="Filter by state"
          @change="filters.state = $event || null; fetchUniversities()"
        />
        <tu-filter-select
          :value="filters.offering"
          label="Offerings"
          placeholder="All"
          :options="offeringOptions"
          aria-label="Filter by offering"
          @change="filters.offering = $event || null; fetchUniversities()"
        />
      </div>

      <div class="tu-results">
        <p class="tu-count" role="status" aria-live="polite">
          <template v-if="isLoading">Loading schools…</template>
          <template v-else-if="totalUniversities">
            Showing {{ pageFromCount }}–{{ pageToCount }} of {{ totalUniversities }}
            {{ selectedList === 'favorites' ? 'favorite schools' : 'schools' }}
          </template>
        </p>
        <div class="tu-results-tools">
          <button v-if="hasActiveFilters" type="button" class="tu-clear" @click="clearFilters">Clear filters <span aria-hidden="true">×</span></button>
          <radio-button-group
            :options="listFilterOptions"
            :primary-color="brandColor"
            name="list-schools"
            :model-value="selectedList"
            @update:modelValue="selectedList = $event"
          />
        </div>
      </div>

      <div v-if="isLoading" class="tu-school-grid" aria-busy="true">
        <div v-for="i in 6" :key="`s-${i}`" class="tu-skel tu-skel--school"></div>
      </div>

      <div v-else-if="!totalUniversities" class="tu-empty">
        <md-icon>{{ selectedList === 'favorites' ? 'favorite_border' : 'school' }}</md-icon>
        <h4 class="tu-empty-title">
          {{ selectedList === 'favorites' ? 'No favorites yet' : 'No results match your filters' }}
        </h4>
        <p class="tu-empty-text" v-if="selectedList === 'favorites'">
          Tap the heart on a school to keep it here for quick access.
        </p>
        <p class="tu-empty-text" v-else>Try clearing a filter or expanding your search.</p>
        <button v-if="selectedList === 'favorites'" type="button" class="tu-btn tu-btn--primary tu-empty-cta" @click="selectedList = 'all'">
          Browse all schools
        </button>
        <button v-else-if="hasActiveFilters" type="button" class="tu-btn tu-btn--ghost tu-empty-cta" @click="clearFilters">
          Clear filters
        </button>
      </div>

      <div v-else class="tu-school-grid" :class="isMobSmallScreen ? 'mb-5' : ''">
        <school-card
          v-for="university in universities"
          :key="university.id"
          :school="university"
          :image="university.campus_img_url || ''"
          :unlocked="enrolledUniversityIds.has(Number(university.id))"
          @view="goToSchool"
          @toggle-fav="toggleFav"
        />
      </div>

      <pagination
        v-if="totalUniversities > universities.length"
        v-model="currentPage"
        :total-rows="totalUniversities"
        :per-page="perPage"
        class="mt-4"
        @change="onPageChange"
        aria-controls="universities-table"
      />
    </div>
  </div>
</template>

<script>
import { debounce, get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import { UsaStates } from 'usa-states';
import Page from '@/components/Page.vue';
import RadioButtonGroup from '@/components/RadioGroup.vue';
import Pagination from '@/components/Ui/Pagination.vue';
import TuFilterSearch from '@/components/Ui/TuFilterSearch.vue';
import TuFilterSelect from '@/components/Ui/TuFilterSelect.vue';
import { UNI_OFFERINGS } from '@/common/constants';
import SchoolCard from './SchoolCard.vue';

/**
 * Student portal Institutions page. Mirrors transitionu.health/institutions:
 * search + program/state/offering filters in a toolbar, a responsive grid of
 * school cards, favorites, and a "View school" action into the school page.
 */
export default {
  name: 'DashUniversitiesList',
  components: { Pagination, RadioButtonGroup, SchoolCard, TuFilterSearch, TuFilterSelect },
  extends: Page,

  data() {
    return {
      title: 'Schools',
      isLoading: true,
      perPage: 24,
      currentPage: 1,
      totalUniversities: 0,
      universities: [],
      searchTerm: '',
      pageFromCount: 0,
      pageToCount: 0,
      windowWidth: window.innerWidth,
      listFilterOptions: [
        { text: 'All', value: 'all' },
        { text: 'Favorites', value: 'favorites' },
      ],
      selectedList: '',
      programs: [],
      enrolledUniversityIds: new Set(),
      filters: {
        offering: null,
        program_id: null,
        state: null,
      },
    };
  },

  computed: {
    brandColor() {
      return get(this.getLoggedInUser, 'linked_entity.primary_color') || '#3b86f5';
    },
    ...mapGetters('auth', ['getLoggedInUser']),

    isMobSmallScreen() {
      return this.windowWidth <= 776;
    },

    programOptions() {
      return this.programs.map(p => ({ value: p.id, text: p.title }));
    },

    stateOptions() {
      return new UsaStates().states.map(s => ({ value: s.name, text: s.name }));
    },

    offeringOptions() {
      // Flatten the grouped offerings into one picker, keeping the group name as context.
      return UNI_OFFERINGS.flatMap(group =>
        group.options.map(o => ({ value: o.value, text: `${o.text} · ${group.label}` })),
      );
    },

    hasActiveFilters() {
      return !!(this.searchTerm || this.filters.program_id || this.filters.state || this.filters.offering);
    },
  },

  watch: {
    selectedList: {
      handler() {
        this.searchTerm = '';
        this.fetchUniversities();
        if (this.$route.query.list !== this.selectedList) {
          this.$router.replace({ query: { list: this.selectedList } });
        }
      },
    },
  },

  methods: {
    ...mapActions('uni', ['getAllUniversities', 'updateFavStatus', 'getFavUniversities', 'setSelectedStudentUniversity']),
    ...mapActions('program', ['getAllPublicPrograms']),
    ...mapActions('pathways', ['getMyPathwayEnrollments']),
    get,

    getSchoolRoute(university) {
      return {
        name: 'std-cc-uni-checklist',
        params: { slug: university.slug },
        ...(this.filters.program_id && { query: { program_id: this.filters.program_id } }),
      };
    },

    async goToSchool(university) {
      await this.setSelectedStudentUniversity(university);
      this.$router.push(this.getSchoolRoute(university));
    },

    async toggleFav(university) {
      const index = this.universities.findIndex(u => u.id === university.id);
      if (index < 0) return;
      try {
        const next = !this.universities[index].fav_status;
        await this.updateFavStatus({ uni_id: university.id, fav_status: next });
        this.$set(this.universities, index, { ...this.universities[index], fav_status: next });
        if (this.selectedList === 'favorites' && !next) {
          this.fetchUniversities(
            this.currentPage > 1 && this.universities.length === 1 ? this.currentPage - 1 : this.currentPage,
          );
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },

    activeFilterParams() {
      const params = {};
      if (this.filters.program_id) params.program_id = this.filters.program_id;
      if (this.filters.state) params.state = this.filters.state;
      if (this.filters.offering) params.offering = this.filters.offering;
      return params;
    },

    clearFilters() {
      this.searchTerm = '';
      this.filters = { offering: null, program_id: null, state: null };
      this.fetchUniversities();
    },

    async fetchUniversities(pageNum = 1, params = {}) {
      if (this.selectedList === 'favorites') {
        await this.fetchFavUniversities(pageNum, params);
      } else {
        await this.fetchAllUniversities(pageNum, params);
      }
    },

    async fetchAllUniversities(pageNum = 1, params = {}) {
      this.isLoading = true;
      try {
        const response = await this.getAllUniversities({
          limit: this.perPage,
          offset: (pageNum - 1) * this.perPage,
          ordering: 'name',
          ...(this.searchTerm && { search: this.searchTerm }),
          ...this.activeFilterParams(),
          ...params,
        });
        this.universities = response.data.results;
        this.currentPage = pageNum;
        this.totalUniversities = response.data.count;
        this.pageFromCount = (this.currentPage - 1) * this.perPage + 1;
        this.pageToCount = this.pageFromCount + get(response.data, 'results.length', 0) - 1;
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    async fetchFavUniversities(pageNum = 1, params = {}) {
      this.isLoading = true;
      try {
        const response = await this.getFavUniversities({
          limit: this.perPage,
          offset: (pageNum - 1) * this.perPage,
          ordering: 'name',
          ...(this.searchTerm && { search: this.searchTerm }),
          ...this.activeFilterParams(),
          ...params,
        });
        this.universities = response.data.results.map(uni => ({ ...uni, fav_status: true }));
        this.currentPage = pageNum;
        this.totalUniversities = response.data.count;
        this.pageFromCount = (this.currentPage - 1) * this.perPage + 1;
        this.pageToCount = this.pageFromCount + get(response.data, 'results.length', 0) - 1;
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    async fetchPrograms() {
      try {
        const response = await this.getAllPublicPrograms({ type: 'degree' });
        const list = get(response, 'data.results', get(response, 'data', [])) || [];
        this.programs = Array.isArray(list) ? list : [];
      } catch (_err) {
        this.programs = [];
      }
    },

    // Schools the student has unlocked through a pathway enrollment (shown as a chip on the card).
    async fetchEnrolledUniversities() {
      try {
        const response = await this.getMyPathwayEnrollments({ limit: 200 });
        const ids = new Set();
        get(response, 'data.results', []).forEach(enrollment => {
          (enrollment.uni_offered_programs || []).forEach(uop => {
            const id = get(uop, 'university.id');
            if (id) ids.add(Number(id));
          });
        });
        this.enrolledUniversityIds = ids;
      } catch (_err) {
        this.enrolledUniversityIds = new Set();
      }
    },

    onPageChange(pageNum) {
      this.fetchUniversities(pageNum);
    },

    onSearchInput(value) {
      this.searchTerm = value;
      this.debouncedSearchUniversities(this);
    },

    debouncedSearchUniversities: debounce(vm => {
      vm.fetchUniversities();
    }, 400),

    handleResize() {
      this.windowWidth = window.innerWidth;
    },
  },

  async mounted() {
    window.addEventListener('resize', this.handleResize);
    if (this.$route.query.program_id) this.filters.program_id = Number(this.$route.query.program_id) || null;
    this.fetchPrograms();
    this.fetchEnrolledUniversities();
    this.selectedList = this.$route.query.list || 'all';
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>

<style lang="scss" scoped>
.tu-skel--school {
  min-height: 300px;
}
</style>
